import { useMemo, useRef, type MutableRefObject } from 'react';

import { DateCellTemplate } from 'components/DateCell';
import { AddRowCellTemplate } from 'components/AddRowCell';
import { CheckboxCellTemplate } from 'components/CheckBoxCell';
import { DeleteRowCellTemplate } from 'components/DeleteRowCell';
import { HeaderRowTemplate } from 'components/HeaderRow';
import { TextCellTemplate } from 'components/TextCell';
import { DropdownCellTemplate } from 'components/DropdownCell';
import { FormulaCellTemplate } from 'components/FormulaCell';

export type PropertyCellTemplateCallbacks = {
  onAddRow: (cell?: unknown) => void;
  onDeleteRowPrompt: (data: any) => void;
};

/**
 * Memoized template instances; callbacks read from `callbacksRef` so ReactGrid keeps a stable `customCellTemplates` reference. Permission flags are synced each render.
 */
export function useStablePropertyCellTemplates(
  planPermissionReadOnly: boolean,
  callbacksReference: MutableRefObject<PropertyCellTemplateCallbacks>,
) {
  const permissionReference = useRef(planPermissionReadOnly);
  permissionReference.current = planPermissionReadOnly;

  const templates = useMemo(() => {
    const addRowTemplate = new AddRowCellTemplate(() => {
      callbacksReference.current.onAddRow();
    }, permissionReference.current);
    const dropdownTemplate = new DropdownCellTemplate(
      (data) => callbacksReference.current.onDeleteRowPrompt(data),
      permissionReference.current,
    );
    return {
      header: new HeaderRowTemplate(),
      delete: new DeleteRowCellTemplate(),
      text: new TextCellTemplate(),
      date: new DateCellTemplate(),
      addRow: addRowTemplate,
      dropdown: dropdownTemplate,
      checkbox: new CheckboxCellTemplate(),
      formula: new FormulaCellTemplate(),
    };
  }, [callbacksReference]);

  templates.addRow.planPermission = planPermissionReadOnly;
  templates.dropdown.planPermission = planPermissionReadOnly;

  return templates;
}
